import React, { useContext, useEffect, useState, useRef } from 'react';

/* eslint-disable max-len,object-curly-newline */
// THD NUCLEUS
import {
  extend, params, string, useDataModel, customType,
  shape, bool, arrayOf, QueryContext, QueryProvider
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useStoreId, useConfigService } from '@thd-nucleus/experience-context';
import { PIPRedirector, DynamicComponent, AppContext } from '@thd-nucleus/app-render';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';

// THD Components (alphabetical by package name)
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { BespokeApplianceCTA } from '@thd-olt-component-react/bespoke-appliance-cta';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { MajorApplianceBuyBox } from '@thd-olt-component-react/buybox';
import { ConsumerCardMessaging } from '@thd-olt-component-react/consumer-card-messaging';
import { Button, Col, Row, Placeholder } from '@thd-olt-component-react/core-ui';
import { DeliveryAndInstallation } from '@thd-olt-component-react/delivery-and-installation';
import { Discontinued } from '@thd-olt-component-react/discontinued';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { GlobalCustomConfigurator } from '@thd-olt-component-react/global-custom-configurator';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { MediaGalleryDesktop } from '@thd-olt-component-react/media-gallery';
import { Metadata, ProductMetadata } from '@thd-olt-component-react/metadata';
import { Price } from '@thd-olt-component-react/price';
import { Link, Accordion, AccordionBody, AccordionHeader } from '@one-thd/sui-atomic-components';
import { ProductConfigurator } from '@thd-olt-component-react/product-configurator';
import { ProductDetails } from '@thd-olt-component-react/product-details';
import { ProductInfoBar } from '@thd-olt-component-react/product-info-bar';
import { ProductOverviewDesktop, ProductOverviewAccordionTitle } from '@thd-olt-component-react/product-overview';
import { Promotion } from '@thd-olt-component-react/promotion';
import { PromotionProductsAnchor } from '@thd-olt-component-react/promotion-products';
import { QuestionsAndAnswersDesktop, QuestionsAndAnswersAccordionTitle } from '@thd-olt-component-react/questions-and-answers';
import { RatingsAndReviewsDesktopImporter, RatingsAndReviewsAccordionTitle } from '@thd-olt-component-react/ratings-and-reviews';
import { RelatedSearchImporter, PopularVariationsImporter } from '@thd-olt-component-react/related-search';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { SalientListItem, SalientPoints } from '@thd-olt-component-react/salient-points';
import { SpecificationsAccordionTitle } from '@thd-olt-component-react/specifications';
import { SponsoredPipBanner, SponsoredCarousel } from '@thd-olt-component-react/sponsored-content';
import { StickyNav } from '@thd-olt-component-react/sticky-nav';
import { SuperSku } from '@thd-olt-component-react/super-sku';
import {
  DynamicRecsWrapper,
  RecentlyViewedWrapper,
  ItemRelatedGroups
} from '@thd-olt-component-react/thd-recs-containers';
import { Sharebelt, ShareBeltPrint, ShareBeltShareButton, ShareFavorite } from '@thd-olt-component-react/thd-sharebelt';
import { WhatWeOffer } from '@thd-olt-component-react/what-we-offer';

// Project Files
import { PackagesMiniABTest } from '../../components/PackagesMiniABTest';
import {
  FeatureBasedRecommendationsLoad, FeatureBasedRecommendations, FeatureBasedRecommendationsModel,
  SpecificationsLoad, Specifications, SpecificationsModel,
  KeyProductFeaturesLoad, KeyProductFeatures,
  QuestionsAndAnswersAccordionBody, QuestionsAndAnswersAccordionBodyLoad,
  RatingsAndReviewsAccordionBody, RatingsAndReviewsAccordionBodyLoad,
  ProductOverviewAccordionBody, ProductOverviewAccordionBodyLoad
} from './dynamic-imports';
import { EmtLinksLoad, EmtLinks, EmtLinksModel } from '../shared-dynamic-imports';
import {
  shouldDisplayVisuallySimilar,
  shouldDisplayIrgaccessories,
  shouldDisplayIrgcollections,
  getIRGSchema
} from './product-utils';
import { updateStickyHeader, updateHeaderConfiguration } from '../utils';
import {
  HYDRATION_STANDARD_SCROLL_BUFFER,
  HYDRATION_STANDARD_DELAY_TIMER,
  RECS_REQUEST_KEY,
  STICKY_HEADER_HEIGHT,
  ONLINE_STORE_ID,
  AVATAAR_AUGMENTED_REALITY_IDS_TNT
} from '../../constants';
import ZoneCard from '../../components/zone-card';
import { useAutoScrollToId } from '../../hooks/useAutoScrollToId';
import { useShouldDisplayReferrerRecommendations } from '../../hooks/useShouldDisplayReferrerRecommendations';
import '../../styles/product-page.scss';

/* eslint-enable max-len,object-curly-newline */

export const Product = () => {
  declareContexts([
    QueryContext,
    ExperienceContext,
    CustomerContext,
  ]);
  const thdCustomer = useThdCustomer();
  const ctx = useContext(ExperienceContext);
  const qctx = useContext(QueryContext);
  const { instance } = useContext(AppContext);
  const thdSeoBotDetected = instance?.thdSEOBotDetection;
  const isB2BCustomer = ctx?.customer?.type === 'b2b';
  const { itemId } = useParams();
  const storeId = useStoreId();
  let navigate = useNavigate();
  const { pathname } = useLocation();

  const [showPackagesMini, setShowPackagesMini] = useState(true);
  const [expandedAccordions, setExpandedAccordions] = useState({});
  const [showAvataarAR, setShowAvataarAR] = useState(false);
  const [showAvataarButton, setshowAvataarButton] = useState(false);

  const stickyNavRef = useRef(null);
  const productOverviewRef = useRef(null);
  const specificationsRef = useRef(null);
  const questionsRef = useRef(null);
  const ratingsRef = useRef(null);
  let fsAvataar = useConfigService('fs:isAvataarEnable');

  const { data: productData, loading } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId || storeId === ONLINE_STORE_ID || !storeId,
    ssr: false
  });

  useEffect(() => {
    updateStickyHeader('Header3-sticky');
    updateHeaderConfiguration('default');
  }, []);

  const containAvataarId = (id) => {
    if (id) {
      return AVATAAR_AUGMENTED_REALITY_IDS_TNT.indexOf(id) > -1;
    }
    return false;
  };

  useEffect(() => {
    if (fsAvataar && !loading && productData && itemId && containAvataarId(itemId)) {
      setshowAvataarButton(true);
    }
  }, [productData]);

  useAutoScrollToId(STICKY_HEADER_HEIGHT.DESKTOP);

  const pipRecsPositionAbove = useConfigService('fs:pipRecsPositionAbove');
  const pipRecsPositionBelow = useConfigService('fs:pipRecsPositionBelow');
  const pipRecsPositionDefault = useConfigService('fs:pipRecsPositionDefault');
  const isPipRecsAbTest = pipRecsPositionAbove || pipRecsPositionBelow || pipRecsPositionDefault;
  const shouldDisplayReferrerRecommendations = useShouldDisplayReferrerRecommendations();
  const shouldDisplayReferrerRecsAbove = (shouldDisplayReferrerRecommendations && !isPipRecsAbTest)
    || pipRecsPositionAbove
  const shouldDisplayReferrerRecsBelow = pipRecsPositionBelow
  const shouldDisplayReferrerRecsDefault = (!shouldDisplayReferrerRecommendations && !isPipRecsAbTest)
    || pipRecsPositionDefault

  const onSuperSkuChange = (data = {}) => {
    const { canonicalUrl } = data;
    if (canonicalUrl) {
      navigate(canonicalUrl, { state: { disableScroll: true } });
    }
  };

  const toggleAccordionOpen = (accordion, { canCloseAccordion = true } = {}) => {
    if (!expandedAccordions[accordion] || canCloseAccordion) {
      setExpandedAccordions((prevState) => ({ ...prevState, [accordion]: !(prevState[accordion]) }));
    }
  };

  const openAccordion = (accordionId) => {
    toggleAccordionOpen(accordionId, { canCloseAccordion: false })
  };

  const handleViewMoreDetails = (event) => {
    event.preventDefault();
    openAccordion('#product-section-overview');
    window.scrollTo({
      top: productOverviewRef.current.offsetTop - STICKY_HEADER_HEIGHT.DESKTOP,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <ErrorBoundary name="redirector">
        <PIPRedirector disablePEPRedirect disableCanonicalRedirect disableCMMMCRedirect />
      </ErrorBoundary>
      <ErrorBoundary name="product-metadata">
        <Metadata>
          <ProductMetadata itemId={itemId} />
        </Metadata>
      </ErrorBoundary>
      <ErrorBoundary name="pip-content">
        <Row className="isBound">
          <Row>
            <ErrorBoundary name="sticky-nav">
              <StickyNav
                itemId={itemId}
                ref={stickyNavRef}
                ratingsRef={ratingsRef}
                onRatingsClick={() => openAccordion('#product-section-rr')}
              >
                <StickyNav.Links>
                  <StickyNav.Link targetRef={productOverviewRef}>
                    <div
                      className="navlink-pso"
                      onClick={() => openAccordion('#product-section-overview')}
                      role="button"
                      tabIndex={0}
                    >Product Details
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={specificationsRef}>
                    <div
                      className="navlink-specs"
                      onClick={() => openAccordion('#product-section-key-feat')}
                      role="button"
                      tabIndex={0}
                    >Specifications
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={questionsRef}>
                    <div
                      className="navlink-qa"
                      onClick={() => openAccordion('#product-section-qa')}
                      role="button"
                      tabIndex={0}
                    >Questions & Answers
                    </div>
                  </StickyNav.Link>
                  <StickyNav.Link targetRef={ratingsRef}>
                    <div
                      className="navlink-rr"
                      onClick={() => openAccordion('#product-section-rr')}
                      role="button"
                      tabIndex={0}
                    >Customer Reviews
                    </div>
                  </StickyNav.Link>
                </StickyNav.Links>
              </StickyNav>
            </ErrorBoundary>
          </Row>
          <Row>
            <ErrorBoundary name="breadcrumbs-product-info-bar">
              <Col sm="7">
                <Breadcrumbs itemId={itemId} useStencilDesign />
              </Col>
              <Col sm="5" className="u__flex-end u__paddingRightNone">
                <ProductInfoBar itemId={itemId} />
              </Col>
            </ErrorBoundary>
          </Row>
          {shouldDisplayReferrerRecsAbove && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pipsem"
                anchorId={itemId}
                hideATC
                requestKey={RECS_REQUEST_KEY}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: 100,
                  id: pipRecsPositionAbove ? 'RecsAbove' : 'pipsem-deskto-b2c'
                }}
              />
            </Row>
          )}
          <Row id="zone-a">
            <ErrorBoundary name="zone-a">
              <Col nopadding>
                <Row>
                  <Col>
                    <ErrorBoundary name="discontinued">
                      <Discontinued itemId={itemId} />
                    </ErrorBoundary>
                  </Col>
                  <Col sm="7" flatten className="product--sticky">
                    <ZoneCard className="u--paddingBottom overflow u__clearfix ">
                      <Col sm="12">
                        <ErrorBoundary name="product-details">
                          <ProductDetails itemId={itemId}>
                            <ProductDetails.Badge itemId={itemId} />
                            <ProductDetails.BrandCollection showCollection showBrandHyperLink />
                            <div className="product-details__badge-title--wrapper">
                              <ProductDetails.Title />
                            </div>
                            { (!isB2BCustomer) && (
                              <div className="product-details__favorites">
                                <Sharebelt itemId={itemId}>
                                  <ShareFavorite showCircle showFavoritesCount />
                                </Sharebelt>
                              </div>
                            ) }
                            <ProductDetails.Ratings
                              showBadge
                              target="#product-section-rr"
                              offsetselector="#sticky-nav"
                              qaselector="#product-section-qa"
                              onClick={openAccordion}
                            />
                          </ProductDetails>
                        </ErrorBoundary>
                      </Col>
                      <ErrorBoundary name="media-gallery">
                        <MediaGalleryDesktop
                          itemId={itemId}
                          mainImageFetchPriority="high"
                          showLoader
                          showAvataarDefault={showAvataarButton}
                          onAvataarButtonClick={() => setShowAvataarAR(false)}
                          showAvataarAR={showAvataarAR}
                        />
                      </ErrorBoundary>
                      <ErrorBoundary name="sharebelt">
                        <Sharebelt horizontal itemId={itemId}>
                          <ShareBeltPrint />
                          <ShareBeltShareButton />
                        </Sharebelt>
                      </ErrorBoundary>
                    </ZoneCard>

                    <ErrorBoundary name="AvataarAR">
                      <ZoneCard className="overflow ar-container col__12-12">
                        {
                          itemId && showAvataarButton && (
                            <Button
                              className="avataar-btn"
                              outline
                              dark
                              onClick={() => setShowAvataarAR(true)}
                            >
                              <div>View in 3D Augmented Reality</div>
                            </Button>
                          )
                        }
                      </ZoneCard>
                    </ErrorBoundary>
                  </Col>
                  <Col sm="5" flatten>
                    <Row className="overflow zone-a">
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="price">
                          <Price
                            channel="desktop"
                            displayEachUom={false}
                            itemId={itemId}
                            large
                            disableRangePricing
                            clsRemediation={{
                              placeholders: true,
                              preservePlaceholders: true
                            }}
                            showPreferredPricingBadge
                            showPreferredPricingBadgeToolTip
                          />
                        </ErrorBoundary>
                        <ErrorBoundary name="promotion">
                          <Col className="overflow promotion__card">
                            <Promotion
                              hideConditionalPromoDescription
                              itemId={itemId}
                            />
                          </Col>
                        </ErrorBoundary>
                        <div className="consumer-credit-messaging__card">
                          <ConsumerCardMessaging
                            itemId={itemId}
                            clsRemediation={{
                              placeholders: true,
                              preservePlaceholders: true
                            }}
                            isB2B={isB2BCustomer}
                          />
                        </div>
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="promotion-products">
                          <PromotionProductsAnchor
                            itemId={itemId}
                            isMultiSelect={(product) => !!product?.pricing?.conditionalPromotions?.[0]?.nvalues?.length}
                          />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="salient-points">
                          <SalientPoints itemId={itemId} max={3}>
                            <SalientListItem>
                              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                              <Link
                                underline="always"
                                onClick={handleViewMoreDetails}
                              >
                                View More Details
                              </Link>
                            </SalientListItem>
                          </SalientPoints>
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="aisle-bay">
                          <AisleBay itemId={itemId} />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col} className="overflow">
                        <ErrorBoundary name="product-configurator">
                          {productData?.product?.identifiers?.productType === 'CONFIGURABLE_BLINDS' && (
                            <ProductConfigurator itemId={itemId} />
                          )}
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col} className="overflow">
                        <ErrorBoundary name="super-sku">
                          <SuperSku
                            itemId={itemId}
                            onChange={onSuperSkuChange}
                            disableMediaAndAvailability
                          />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col}>
                        <ErrorBoundary name="bespoke-appliance-cta">
                          <BespokeApplianceCTA itemId={itemId} />
                        </ErrorBoundary>
                      </ZoneCard>
                      <ZoneCard tag={Col} nopadding>
                        <MajorApplianceBuyBox itemId={itemId} sticky showCards showFavorites={isB2BCustomer} />
                      </ZoneCard>
                      <Col>
                        <ErrorBoundary name="global-custom-configurator">
                          <GlobalCustomConfigurator itemId={itemId} />
                        </ErrorBoundary>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </ErrorBoundary>
          </Row>
          {shouldDisplayReferrerRecsBelow && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pipsem"
                anchorId={itemId}
                hideATC
                requestKey={RECS_REQUEST_KEY}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: 100,
                  id: 'RecsBelow'
                }}
              />
            </Row>
          )}
          <ErrorBoundary name="collapsible-sections">
            <DynamicComponent pageType="pip">
              <Hydrator
                id="collapsible-sections-accordion"
                className="grid"
                placeholder={(
                  <>
                    {[
                      { id: 'product-section-overview', ref: productOverviewRef },
                      { id: 'product-section-key-feat', ref: specificationsRef },
                      { id: 'product-section-qa', ref: questionsRef },
                      { id: 'product-section-rr', ref: ratingsRef },
                    ].map(({ id, ref }) => (
                      <div id={id} ref={ref} className="accordion-placeholder" key={id}>
                        <Placeholder width="100%" height="82px" color="#F5F5F5" />
                      </div>
                    ))}
                  </>
                )}
              >
                <div className="sui-flex sui-flex-col sui-w-full">
                  <div
                    id="product-section-overview"
                    className="product-section-overview"
                    ref={productOverviewRef}
                  >
                    <Accordion
                      key="product-details-accordion"
                      expanded={expandedAccordions['#product-section-overview'] || thdSeoBotDetected}
                      onChange={() => toggleAccordionOpen('#product-section-overview')}
                    >
                      <AccordionHeader>
                        <ProductOverviewAccordionTitle />
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-product-overview"
                            className="grid"
                            waitFor={ProductOverviewAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <ProductOverviewAccordionBody
                              itemId={itemId}
                              showReturnMessage
                              revJet
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-key-feat"
                    className="product-section-key-feat"
                    ref={specificationsRef}
                  >
                    <Accordion
                      key="specifications-accordion"
                      expanded={expandedAccordions['#product-section-key-feat'] || thdSeoBotDetected}
                      onChange={() => toggleAccordionOpen('#product-section-key-feat')}
                    >
                      <AccordionHeader>
                        <div className="sui-flex sui-flex-col">
                          <SpecificationsAccordionTitle itemId={itemId} />
                        </div>
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="specifications-desktop"
                            className="grid"
                            delay={HYDRATION_STANDARD_DELAY_TIMER}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#f5f5f5"
                              >
                                <div />
                              </Placeholder>
                            )}
                            preserveCtxVal="clientStore"
                            scrollBuffer={500}
                            waitFor={[SpecificationsLoad, KeyProductFeaturesLoad]}
                          >
                            <ZoneCard tag={Row}>
                              <ErrorBoundary name="specifications">
                                <Specifications itemId={itemId} qctx={qctx}>
                                  <KeyProductFeatures itemId={itemId} displaySimilarLinks={false} qctx={qctx} />
                                </Specifications>
                              </ErrorBoundary>
                            </ZoneCard>
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-qa"
                    className="product-section-qa"
                    ref={questionsRef}
                  >
                    <Accordion
                      key="questions-answers-accordion"
                      expanded={expandedAccordions['#product-section-qa'] || thdSeoBotDetected}
                      onChange={() => toggleAccordionOpen('#product-section-qa')}
                    >
                      <AccordionHeader>
                        <QuestionsAndAnswersAccordionTitle
                          noSpacing
                          itemId={itemId}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-questions-answers"
                            className="grid"
                            waitFor={QuestionsAndAnswersAccordionBodyLoad}
                            placeholder={(<Placeholder width="100%" height="700px" color="#F5F5F5" />)}
                          >
                            <QuestionsAndAnswersAccordionBody
                              itemId={itemId}
                              seoPageNumber={1}
                              qctx={qctx}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                  <div
                    id="product-section-rr"
                    className="product-section-rr"
                    ref={ratingsRef}
                  >
                    <Accordion
                      key="ratings-reviews-accordion"
                      expanded={expandedAccordions['#product-section-rr'] || thdSeoBotDetected}
                      onChange={() => toggleAccordionOpen('#product-section-rr')}
                    >
                      <AccordionHeader>
                        <RatingsAndReviewsAccordionTitle
                          noSpacing
                          itemId={itemId}
                          hydrator={{
                            className: 'grid'
                          }}
                          dynamic={{
                            pageType: 'pip'
                          }}
                        />
                      </AccordionHeader>
                      <AccordionBody>
                        <DynamicComponent pageType="pip">
                          <Hydrator
                            id="product-section-ratings-reviews"
                            className="grid"
                            waitFor={RatingsAndReviewsAccordionBodyLoad}
                            placeholder={(
                              <Placeholder
                                width="100%"
                                height="2000px"
                                color="#F5F5F5"
                              />
                            )}
                          >
                            <RatingsAndReviewsAccordionBody
                              itemId={itemId}
                              qctx={qctx}
                              prosAndCons
                              disableOverlayOnClick={isB2BCustomer}
                            />
                          </Hydrator>
                        </DynamicComponent>
                      </AccordionBody>
                    </Accordion>
                  </div>
                </div>
              </Hydrator>
            </DynamicComponent>
          </ErrorBoundary>

          {showPackagesMini ? (
            <ZoneCard>
              <PackagesMiniABTest
                itemId={itemId}
                dynamic={{
                  pageType: 'pip'
                }}
                hydrator={{
                  className: 'grid',
                }}
                callOnLoaded={({ didRender }) => {
                  setShowPackagesMini(didRender);
                }}
              />
            </ZoneCard>
          ) : (
            <ZoneCard>
              <FrequentlyBoughtTogether
                itemId={itemId}
                onLoadDynamicRecs={() => { }}
                hydrator
              />
            </ZoneCard>
          )}

          <SponsoredPipBanner
            browserId={thdCustomer?.mcvisID}
            pageContext={{
              label: 'pip',
              data: {
                itemId,
                treatment: 'A'
              },
            }}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              id: 'hydrated-sponsored-pip-banner-A',
              placeholder: (
                <Placeholder
                  width="100%"
                  height="144px"
                  color="#f5f5f5"
                >
                  <div />
                </Placeholder>
              )
            }}
          />
          { !isB2BCustomer && (
            <>
              <ZoneCard>
                <ErrorBoundary name="what-we-offer">
                  <WhatWeOffer itemId={itemId} />
                </ErrorBoundary>
              </ZoneCard>
              <ZoneCard>
                <ErrorBoundary name="delivery-and-installation">
                  <DeliveryAndInstallation itemId={itemId} />
                </ErrorBoundary>
              </ZoneCard>
            </>
          )}

          {shouldDisplayReferrerRecsDefault && (
            <Row>
              <DynamicRecsWrapper
                schemaName="pip_alternatives"
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hideATC
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: pipRecsPositionDefault ? 'RecsDefault' : 'pip_alternatives'
                }}
              />
            </Row>
          )}
          <SponsoredCarousel
            browserId={thdCustomer?.mcvisID}
            userId={thdCustomer?.svocID || thdCustomer?.userID || thdCustomer?.guestUserID}
            breadCrumbs={productData?.product?.taxonomy?.breadCrumbs}
            pageContext={{
              schema: 'pip_sponsored',
              data: { itemId },
              isPip: true,
              page: 'pip',
            }}
            showSponsoredCarousel
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'grid',
              scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
            }}
          />
          <DynamicComponent pageType="pip">
            <Hydrator
              id="product-section-fbr"
              className="grid"
              delay={HYDRATION_STANDARD_DELAY_TIMER}
              placeholder={(
                <Placeholder
                  width="100%"
                  height="1040px"
                  color="#f5f5f5"
                >
                  <div />
                </Placeholder>
              )}
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              waitFor={FeatureBasedRecommendationsLoad}
              preserveCtxVal="clientStore"
            >
              <ZoneCard>
                <ErrorBoundary name="feature-based-recommendations">
                  <FeatureBasedRecommendations
                    hideOnMobile
                    itemId={itemId}
                    qctx={qctx}
                  />
                </ErrorBoundary>
              </ZoneCard>
            </Hydrator>
          </DynamicComponent>
          {shouldDisplayVisuallySimilar(productData) && (
            <Row>
              <DynamicRecsWrapper
                schemaName="visuallysimilar_1_0"
                anchorImage={productData?.product?.media?.image?.url}
                anchorId={itemId}
                errorBoundary
                requestKey={RECS_REQUEST_KEY}
                hydrator={{
                  className: 'grid',
                  tag: ZoneCard,
                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                  id: 'visuallysimilar_1_0'
                }}
              />
            </Row>
          )}
          {(shouldDisplayIrgcollections(productData)
            || shouldDisplayIrgaccessories(productData)) && (
            <ItemRelatedGroups
              IRGSchemaObj={getIRGSchema(productData)}
              anchorId={itemId}
              isIRG
              errorBoundary
              schemaName=""
              maxResults="18"
              hideFavorites={false}
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                id: 'itemRelatedGroups',
                delay: 1500,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
              }}
            />
          )}
          <QueryProvider cacheKey="rv-pip-recs" persist>
            <RecentlyViewedWrapper
              schemaName="rv_gm_pip_rr"
              errorBoundary
              hydrator={{
                className: 'grid',
                tag: ZoneCard,
                delay: 2000,
                scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER,
                id: 'rv_gm_pip_rr'
              }}
            />
          </QueryProvider>

          <RelatedSearchImporter
            itemId={itemId}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'col__12-12 sui-mb-4'
            }}
          />

          <PopularVariationsImporter
            productId={itemId}
            dynamic={{
              pageType: 'pip'
            }}
            hydrator={{
              className: 'col__12-12 sui-mb-4'
            }}
          />

          <DynamicComponent pageType="pip">
            <Hydrator
              id="product-section-seo"
              className="grid u__flex-center"
              delay={2000}
              placeholder={(
                <Placeholder
                  width="100%"
                  height="689px"
                  color="#f5f5f5"
                >
                  <div />
                </Placeholder>
              )}
              preserveCtxVal="clientStore"
              scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              waitFor={[EmtLinksLoad]}
            >
              <EmtLinks path={pathname} qctx={qctx} />
            </Hydrator>
          </DynamicComponent>
        </Row>
      </ErrorBoundary>
    </>
  );
};

Product.displayName = 'ProductPage';

Product.dataModel = extend(
  {
    product: params({ itemId: string().isRequired() }).shape({
      fulfillment: params({ storeId: string(), zipCode: string() }).shape({
        backordered: bool()
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired,
      loyaltyMembershipInput: customType('LoyaltyMembershipInput').shape({
        svocID: string(),
        programTiers: arrayOf(shape({
          tier: string(),
          program: string()
        }))
      }) }).shape({
      itemId: string(),
      dataSources: string(),
      info: shape({
        recommendationFlags: shape({
          visualNavigation: bool(),
          ACC: bool(),
          collections: bool(),
        })
      }),
      availabilityType: shape({
        discontinued: bool(),
        type: string()
      }),
      fulfillment: params({ storeId: string() }).shape({
        backordered: bool(),
        fulfillmentOptions: arrayOf(shape({
          type: string(),
          fulfillable: bool(),
          services: arrayOf(shape({
            type: string(),
            locations: arrayOf(shape({
              isAnchor: bool(),
              inventory: shape({
                isLimitedQuantity: bool(),
                isOutOfStock: bool()
              })
            }))
          }))
        })),
      })
    })
  },
  {
    clientOnlyProduct: params({ itemId: string().isRequired }).shape({
      seoDescription: string()
    })
  },
  PIPRedirector,
  ProductMetadata,
  StickyNav,
  Breadcrumbs,
  Sharebelt,
  Discontinued,
  ProductInfoBar,
  MediaGalleryDesktop,
  AisleBay,
  ProductDetails,
  SalientPoints,
  Price,
  Promotion,
  PromotionProductsAnchor,
  ConsumerCardMessaging,
  SuperSku,
  MajorApplianceBuyBox,
  GlobalCustomConfigurator,
  FrequentlyBoughtTogether,
  ProductOverviewDesktop,
  FeatureBasedRecommendationsModel,
  SpecificationsModel,
  QuestionsAndAnswersDesktop,
  RatingsAndReviewsDesktopImporter,
  EmtLinksModel,
  RelatedSearchImporter,
  PopularVariationsImporter,
  DeliveryAndInstallation,
  WhatWeOffer,
  DynamicRecsWrapper,
  RecentlyViewedWrapper,
  PackagesMiniABTest,
  ItemRelatedGroups
);
